/**
 * Safe JSON parser for fetch responses.
 *
 * Problem: When the Emergent preview proxy or backend returns HTML
 * (e.g., "The preview environment is currently paused..." or a 502 error page),
 * calling response.json() throws a SyntaxError with the raw HTML snippet
 * in the message (e.g., Unexpected token 'T', "The previe"... is not valid JSON).
 * That cryptic message ends up in user-facing toasts.
 *
 * This helper:
 *  - Returns { ok, data, error, status } without ever throwing a JSON parse error.
 *  - Detects non-JSON content type and reports a clean error.
 *  - Reports HTTP-level failures as well.
 */
export async function parseJsonSafe(response) {
  const status = response?.status;
  const contentType = (response?.headers?.get?.('content-type') || '').toLowerCase();

  try {
    const text = await response.text();

    // Empty body
    if (!text || !text.trim()) {
      return { ok: response.ok, data: null, error: response.ok ? null : 'Empty response from server', status };
    }

    // Non-JSON response (HTML from proxy, plain text, etc.)
    const looksLikeJson = text.trim().startsWith('{') || text.trim().startsWith('[');
    if (!contentType.includes('application/json') && !looksLikeJson) {
      return {
        ok: false,
        data: null,
        error: 'Server is temporarily unavailable. Please retry.',
        status
      };
    }

    try {
      const data = JSON.parse(text);
      return { ok: response.ok, data, error: response.ok ? null : (data?.detail || data?.message || `Request failed (${status})`), status };
    } catch {
      return {
        ok: false,
        data: null,
        error: 'Server returned invalid data. Please retry.',
        status
      };
    }
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: 'Network error. Please check your connection.',
      status
    };
  }
}

/**
 * Sanitize an error message so cryptic JSON parse errors never reach users.
 */
export function sanitizeErrorMessage(message, fallback = 'Something went wrong. Please try again.') {
  if (!message || typeof message !== 'string') return fallback;
  const lower = message.toLowerCase();
  if (
    lower.includes('unexpected token') ||
    lower.includes('is not valid json') ||
    lower.includes('json.parse') ||
    lower.includes('syntaxerror')
  ) {
    return fallback;
  }
  return message;
}
